/**
 * Getting a pitch deck to the analysis pipeline.
 *
 * The deck goes to Storage, and only a REFERENCE goes to the edge function.
 *
 * The previous transport base64-encoded the file into a JSON field. That is
 * what killed the 18.5 MB Octotronic deck on 2026-08-01: base64 inflates by
 * 4/3, the isolate holds the result as a UTF-16 string, and between the raw
 * body, the decoded bytes, the AV scanner's copy and the retained string for
 * the provider upload, a 256 MB ceiling is reached by the transport alone —
 * before any analysis begins. See `__tests__/deck-upload-transport.test.ts`.
 *
 * The same reference is reused for RAG indexing, so the bytes cross the network
 * once rather than twice.
 */

// A leaf module on purpose: no imports with side effects, so the transport
// invariant can be tested without booting the Supabase client (and through it
// SecureStore, which has no life outside a device). The concrete wiring is
// resolved lazily in `defaultUploadDeps`.

/** Private bucket holding decks an investor uploaded for analysis. */
export const DECK_BUCKET = 'investor-decks';

export interface DeckStorageRef {
  bucket: string;
  path: string;
  /** The only part of this the server will accept from us. */
  analysisId: string;
}

export interface UploadDeckInput {
  fileUri: string;
  fileName: string;
  mimeType: string;
  /** auth.users.id of the investor doing the upload — scopes the object. */
  ownerId: string;
  /** Stable per-attempt id; also the analysis id, so a retry is traceable. */
  requestId: string;
}

export interface UploadDeps {
  /** Read the picked file as bytes. Never as base64. */
  readFile: (uri: string) => Promise<Blob | ArrayBuffer>;
  /** Bound storage upload. Resolves with `{ error }` — never throws. */
  upload: (
    bucket: string,
    path: string,
    body: Blob | ArrayBuffer,
    contentType: string,
  ) => Promise<{ error: Error | null }>;
}

/**
 * Strip anything that would let a filename escape its prefix or confuse the
 * storage key. Deliberately conservative — the name is cosmetic here, since
 * identity comes from `requestId`.
 */
function safeName(fileName: string): string {
  return fileName.replace(/[^A-Za-z0-9._-]/g, '_').slice(-120);
}

/**
 * Object key for a deck. Prefixed with the uploader's id so storage RLS can
 * grant read/write on `owner/**` without publishing the object: an investor
 * routinely uploads a THIRD PARTY's deck, which must not become visible to that
 * third party (or anyone else) as a side effect of being analysed.
 */
export function deckStoragePath(input: UploadDeckInput): string {
  return `${input.ownerId}/${input.requestId}-${safeName(input.fileName)}`;
}

export async function uploadDeckForAnalysis(
  input: UploadDeckInput,
  deps: UploadDeps,
): Promise<DeckStorageRef> {
  const body = await deps.readFile(input.fileUri);
  const path = deckStoragePath(input);

  const { error } = await deps.upload(DECK_BUCKET, path, body, input.mimeType);

  // supabase-js RESOLVES with `{ error }` instead of throwing (ARCH-06), so an
  // unbound result would sail past `await` and hand the server a path with no
  // object behind it. The analysis would then fail deep in the pipeline with a
  // reason that has nothing to do with the real cause.
  if (error) {
    throw new Error(`Deck upload failed: ${error.message}`);
  }

  return { bucket: DECK_BUCKET, path, analysisId: input.requestId };
}

/**
 * The analyse request. Carries an OPAQUE ID and metadata — never a document,
 * and never a location.
 *
 * The bucket and the tenant prefix are deliberately absent. `investor-analyze-deck`
 * downloads under the service role, which bypasses RLS, so a client-supplied
 * path would let an investor name a founder's `pitch-decks` object and have it
 * fetched and extracted for them. The server rebuilds
 * `investor-decks/{auth.uid()}/{analysisId}-...` from the authenticated session,
 * which leaves no cross-tenant path to construct — the same property
 * `pitch-deck-extract` gets from deriving `${user.id}/${documentId}`.
 *
 * Its serialized size must not vary with the deck (see the transport test).
 */
export function buildAnalyzeRequest(
  ref: DeckStorageRef,
  fileName: string,
  mimeType: string,
) {
  return {
    analysisId: ref.analysisId,
    fileName,
    mimeType,
  };
}

/**
 * Platform-appropriate reading. Both branches produce bytes directly; neither
 * goes via base64.
 */
export function defaultUploadDeps(): UploadDeps {
  return {
    readFile: async (uri: string) => {
      // Works for blob: and file: URIs on web, and for file: URIs under the RN
      // networking stack on iOS/Android.
      const response = await fetch(uri);
      return await response.blob();
    },
    upload: async (bucket, path, body, contentType) => {
      const { supabase } = await import('@/lib/supabase/client');
      const { error } = await supabase.storage
        .from(bucket)
        .upload(path, body, { contentType, upsert: true });
      return { error: error ? new Error(error.message) : null };
    },
  };
}
